import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  BookOpen,
  CheckCircle2,
  ClipboardCheck,
  Download,
  Info,
  Loader2,
  Save,
  Scale,
} from "lucide-react";
import { toast } from "sonner";
import {
  exportCcmaForm711Pdf,
  getActiveGuidanceBySlug,
  getLabourAnalysis,
  saveCcmaForm711,
} from "@/lib/api/labour-analyzer.functions";
import { downloadBase64File } from "@/lib/download-base64";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/_authenticated/portal/labour-analyzer/$id/form-7-11")({
  head: () => ({
    meta: [
      { title: "CCMA Form 7.11 wizard | NDLCS" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: Form711Wizard,
});

type Fields = {
  referrer_name: string;
  referrer_id: string;
  referrer_address: string;
  referrer_phone: string;
  referrer_email: string;
  referrer_union: string;
  employer_name: string;
  employer_address: string;
  employer_phone: string;
  employer_email: string;
  dispute_category: string;
  dispute_subcategory: string;
  date_of_dispute: string;
  date_aware: string;
  summary_of_facts: string;
  outcome_sought: string;
  relief_amount: string;
  needs_condonation: boolean;
  condonation_reason: string;
  region_office: string;
  signed_at: string;
  signed_place: string;
};

const EMPTY: Fields = {
  referrer_name: "",
  referrer_id: "",
  referrer_address: "",
  referrer_phone: "",
  referrer_email: "",
  referrer_union: "",
  employer_name: "",
  employer_address: "",
  employer_phone: "",
  employer_email: "",
  dispute_category: "",
  dispute_subcategory: "",
  date_of_dispute: "",
  date_aware: "",
  summary_of_facts: "",
  outcome_sought: "",
  relief_amount: "",
  needs_condonation: false,
  condonation_reason: "",
  region_office: "",
  signed_at: "",
  signed_place: "",
};

const DISPUTE_CATEGORIES = [
  "Unfair dismissal — misconduct",
  "Unfair dismissal — incapacity (ill-health / poor performance)",
  "Unfair dismissal — operational requirements (s189 / s189A)",
  "Unfair dismissal — automatically unfair (discrimination, union activity, pregnancy)",
  "Unfair dismissal — constructive dismissal",
  "Unfair labour practice (s186(2))",
  "Unilateral change to terms & conditions (s64(4))",
  "Severance pay (BCEA s41)",
  "Discrimination / harassment (EEA)",
  "Mutual interest dispute",
  "Other",
];

const STEPS = [
  { id: "guide", title: "How this works", icon: BookOpen },
  { id: "referrer", title: "A. Referring party", icon: Info },
  { id: "employer", title: "B. Other party", icon: Info },
  { id: "nature", title: "C. Nature of dispute", icon: Scale },
  { id: "facts", title: "D. Summary of facts", icon: Info },
  { id: "outcome", title: "E. Outcome sought", icon: Info },
  { id: "dates", title: "F. Dates & time-bar", icon: Info },
  { id: "signature", title: "G. Signature & filing", icon: Info },
  { id: "review", title: "Review & save", icon: ClipboardCheck },
] as const;

// ---- validation helpers ---------------------------------------------------

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^[+0-9 ()\-]{7,20}$/;
const SA_ID_RE = /^\d{13}$/;

type Errors = Partial<Record<keyof Fields, string>> & { _form?: string[] };

function parseDate(v: string): Date | null {
  if (!DATE_RE.test(v)) return null;
  const d = new Date(v + "T00:00:00Z");
  return Number.isNaN(d.getTime()) ? null : d;
}

function daysBetween(a: Date, b: Date) {
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

function validateStep(step: number, f: Fields): Errors {
  const e: Errors = {};
  const req = (k: keyof Fields, msg = "Required") => {
    if (!String(f[k] ?? "").trim()) e[k] = msg;
  };

  if (step === 1) {
    req("referrer_name", "Full name is required");
    req("referrer_id", "ID number is required");
    if (f.referrer_id && !SA_ID_RE.test(f.referrer_id.replace(/\s/g, "")))
      e.referrer_id = "Enter a 13-digit SA ID number";
    req("referrer_address", "Address is required for service of CCMA notices");
    req("referrer_phone", "Phone is required");
    if (f.referrer_phone && !PHONE_RE.test(f.referrer_phone))
      e.referrer_phone = "Use digits, spaces, + or - only";
    req("referrer_email", "Email is required");
    if (f.referrer_email && !EMAIL_RE.test(f.referrer_email))
      e.referrer_email = "Enter a valid email address";
  }

  if (step === 2) {
    req("employer_name", "Employer name is required (use the full registered name)");
    req("employer_address", "Employer address is required");
    if (f.employer_phone && !PHONE_RE.test(f.employer_phone))
      e.employer_phone = "Use digits, spaces, + or - only";
    if (f.employer_email && !EMAIL_RE.test(f.employer_email))
      e.employer_email = "Enter a valid email address";
    if (!f.employer_phone && !f.employer_email)
      e.employer_email = "Provide at least one contact channel for service (email or phone)";
  }

  if (step === 3) {
    req("dispute_category", "Pick a dispute category");
    req("dispute_subcategory", "Briefly state the reason / sub-category");
  }

  if (step === 4) {
    req("summary_of_facts", "A factual summary is required");
    if (f.summary_of_facts && f.summary_of_facts.trim().length < 80)
      e.summary_of_facts =
        "Add more detail — at least a short paragraph (who, what, when, where, why).";
  }

  if (step === 5) {
    req("outcome_sought", "State the relief you want the CCMA to order");
  }

  if (step === 6) {
    req("date_of_dispute", "Date of dismissal / dispute is required");
    if (f.date_of_dispute && !parseDate(f.date_of_dispute))
      e.date_of_dispute = "Use YYYY-MM-DD format (e.g. 2026-06-12)";
    if (f.date_aware && !parseDate(f.date_aware))
      e.date_aware = "Use YYYY-MM-DD format";

    const d1 = parseDate(f.date_of_dispute);
    const d2 = parseDate(f.date_aware);
    const today = new Date();
    if (d1 && d1.getTime() > today.getTime() + 86_400_000)
      e.date_of_dispute = "Date cannot be in the future";
    if (d1 && d2 && d2.getTime() < d1.getTime())
      e.date_aware = "Date aware cannot be before the date of dispute";

    if (f.needs_condonation && !f.condonation_reason.trim())
      e.condonation_reason = "Set out the condonation grounds (Melane v Santam factors)";
  }

  if (step === 7) {
    req("region_office", "Choose the CCMA region office");
    req("signed_place", "Place signed is required");
    req("signed_at", "Date signed is required");
    if (f.signed_at && !parseDate(f.signed_at))
      e.signed_at = "Use YYYY-MM-DD format";
  }

  return e;
}

function validateAll(f: Fields): Errors {
  const merged: Errors = {};
  for (let s = 1; s <= 7; s++) Object.assign(merged, validateStep(s, f));

  // Cross-step consistency
  const formErrors: string[] = [];
  const d1 = parseDate(f.date_of_dispute);
  const signed = parseDate(f.signed_at);
  if (d1 && signed && signed.getTime() < d1.getTime()) {
    merged.signed_at = "Date signed cannot be earlier than the date of dispute";
  }
  if (d1) {
    const today = new Date();
    const daysLate = daysBetween(d1, today);
    const isUlp = /unfair labour practice/i.test(f.dispute_category);
    const limit = isUlp ? 90 : 30;
    if (daysLate > limit && !f.needs_condonation) {
      formErrors.push(
        `The dispute appears to be ${daysLate} days old — over the ${limit}-day time-bar. Tick "filing late" and add a condonation application before saving.`,
      );
    }
  }
  if (f.referrer_email && f.employer_email && f.referrer_email.trim() === f.employer_email.trim()) {
    formErrors.push("Referrer and employer emails are identical — double-check that's correct.");
  }
  if (formErrors.length) merged._form = formErrors;
  return merged;
}

function FieldError({ msg }: { msg?: string }) {
  if (!msg) return null;
  return (
    <p className="mt-1 flex items-start gap-1 text-[11px] text-red-600">
      <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
      {msg}
    </p>
  );
}

function Help({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900">
      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <div className="prose prose-xs max-w-none [&_strong]:text-navy">{children}</div>
    </div>
  );
}

function Form711Wizard() {
  const { id } = Route.useParams();
  const nav = useNavigate();
  const qc = useQueryClient();

  const fetchOne = useServerFn(getLabourAnalysis);
  const fetchGuide = useServerFn(getActiveGuidanceBySlug);
  const save = useServerFn(saveCcmaForm711);
  const exportPdf = useServerFn(exportCcmaForm711Pdf);

  const matterQ = useQuery({
    queryKey: ["labour-analysis", id],
    queryFn: () => fetchOne({ data: { id } }),
  });
  const guideQ = useQuery({
    queryKey: ["guidance", "ccma_form_7_11_guidance"],
    queryFn: () => fetchGuide({ data: { slug: "ccma_form_7_11_guidance" } }),
  });

  const seeded = useMemo<Fields>(() => {
    const row = matterQ.data?.row;
    const report = row?.ai_report ?? {};
    const dates = Array.isArray(report.dates) ? report.dates : [];
    const dDis =
      dates.find((d: any) => /dismiss|incident/i.test(d.label))?.value ?? "";
    return {
      ...EMPTY,
      referrer_name: report.parties?.employee ?? "",
      employer_name: report.parties?.employer ?? "",
      dispute_category: report.ccma_opportunity?.category
        ? `Unfair dismissal — ${report.ccma_opportunity.category}`
        : "",
      date_of_dispute: DATE_RE.test(dDis) ? dDis : "",
      summary_of_facts: report.matter_summary ?? "",
      outcome_sought:
        report.ccma_opportunity?.applicable
          ? "Reinstatement, alternatively maximum compensation."
          : "",
    };
  }, [matterQ.data?.row?.id]);

  const [fields, setFields] = useState<Fields>(EMPTY);
  const [seededOnce, setSeededOnce] = useState(false);
  useEffect(() => {
    if (matterQ.data?.row && !seededOnce) {
      setFields(seeded);
      setSeededOnce(true);
    }
  }, [matterQ.data?.row?.id, seeded, seededOnce]);

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [showAll, setShowAll] = useState(false);

  function up<K extends keyof Fields>(k: K, v: Fields[K]) {
    setFields((f) => ({ ...f, [k]: v }));
    // clear that field's error inline
    setErrors((e) => {
      if (!e[k]) return e;
      const { [k]: _drop, ...rest } = e;
      return rest;
    });
  }

  function tryNext() {
    const e = validateStep(step, fields);
    setErrors(e);
    if (Object.keys(e).length) {
      toast.error("Please correct the highlighted fields before continuing.");
      return;
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  }

  async function onSave() {
    const all = validateAll(fields);
    setErrors(all);
    setShowAll(true);
    const fieldKeys = Object.keys(all).filter((k) => k !== "_form");
    if (fieldKeys.length) {
      toast.error("Some required fields are missing or invalid — see the review checklist.");
      return;
    }
    if (all._form?.length) {
      toast.error(all._form[0]);
      return;
    }
    setSaving(true);
    try {
      const { draft } = await save({ data: { analysisId: id, fields } });
      toast.success("Form 7.11 saved as a draft on this matter");
      qc.invalidateQueries({ queryKey: ["labour-analysis", id] });
      nav({
        to: "/portal/labour-analyzer/$id" as any,
        params: { id } as any,
      });
      return draft;
    } catch (e: any) {
      toast.error(e?.message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function onDownloadPdf() {
    const all = validateAll(fields);
    setErrors(all);
    setShowAll(true);
    const fieldKeys = Object.keys(all).filter((k) => k !== "_form");
    if (fieldKeys.length) {
      toast.error("Fix the highlighted fields before downloading the PDF.");
      return;
    }
    setDownloading(true);
    try {
      const out = await exportPdf({ data: { analysisId: id, fields } });
      downloadBase64File(out.base64, out.filename);
      toast.success("PDF download started");
    } catch (e: any) {
      toast.error(e?.message ?? "Could not generate PDF");
    } finally {
      setDownloading(false);
    }
  }

  if (matterQ.isLoading || !matterQ.data?.row) {
    return (
      <p className="text-sm text-muted-foreground">
        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading matter…
      </p>
    );
  }

  const guide = guideQ.data?.template;
  const current = STEPS[step];
  const stepErrorCount = Object.keys(errors).filter((k) => k !== "_form").length;

  return (
    <div className="space-y-6">
      <Link
        to={"/portal/labour-analyzer/$id" as any}
        params={{ id } as any}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-navy"
      >
        <ArrowLeft className="h-4 w-4" /> Back to matter
      </Link>

      <header className="rounded-2xl border border-border bg-gradient-to-br from-navy/[0.04] to-gold/[0.06] p-6 shadow-soft">
        <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-gold">
          <Scale className="h-4 w-4" /> CCMA Form LRA 7.11 — Wizard
        </div>
        <h1 className="font-display text-2xl text-navy">
          {matterQ.data.row.matter_title}
        </h1>
        <p className="mt-1 text-xs text-muted-foreground">
          Step through each section. Inline guidance is loaded from the NDLCS labour-law
          knowledge base ({guide ? `v${guide.version}` : "loading…"}).
        </p>
      </header>

      {/* Stepper */}
      <ol className="flex flex-wrap gap-1 text-[11px]">
        {STEPS.map((s, i) => (
          <li key={s.id}>
            <button
              type="button"
              onClick={() => setStep(i)}
              className={`rounded-full border px-2.5 py-1 ${
                i === step
                  ? "border-navy bg-navy text-navy-foreground"
                  : i < step
                  ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                  : "border-input bg-background text-muted-foreground"
              }`}
            >
              {i + 1}. {s.title}
            </button>
          </li>
        ))}
      </ol>

      <section className="space-y-4 rounded-xl border border-border bg-card p-5 shadow-soft">
        <h2 className="text-sm font-semibold text-navy">
          <current.icon className="mr-1 inline h-4 w-4" /> {current.title}
        </h2>

        {step === 0 && (
          <div className="space-y-3">
            <Help>
              <p>
                Form <strong>LRA 7.11</strong> is the official referral form used to start a
                CCMA matter. This wizard walks you through each section with plain-English
                guidance, validates each input, and saves a fully drafted document — and a
                downloadable PDF — to your matter.
              </p>
            </Help>
            {guide?.body_md ? (
              <pre className="max-h-[400px] overflow-auto whitespace-pre-wrap rounded-md border border-border bg-muted/30 p-3 text-xs text-foreground/90">
                {guide.body_md}
              </pre>
            ) : (
              <p className="text-xs text-muted-foreground">
                <Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> Loading guidance…
              </p>
            )}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3">
            <Help>
              The <strong>referring party</strong> is the person bringing the dispute. Use
              the name on your ID and payslip. Service of all CCMA correspondence will go
              to the address and email you give.
            </Help>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label>Full name *</Label>
                <Input value={fields.referrer_name} onChange={(e) => up("referrer_name", e.target.value)} aria-invalid={!!errors.referrer_name} />
                <FieldError msg={errors.referrer_name} />
              </div>
              <div>
                <Label>ID number *</Label>
                <Input value={fields.referrer_id} onChange={(e) => up("referrer_id", e.target.value)} aria-invalid={!!errors.referrer_id} inputMode="numeric" maxLength={13} />
                <FieldError msg={errors.referrer_id} />
              </div>
              <div className="md:col-span-2">
                <Label>Address (physical & postal) *</Label>
                <Textarea rows={2} value={fields.referrer_address} onChange={(e) => up("referrer_address", e.target.value)} aria-invalid={!!errors.referrer_address} />
                <FieldError msg={errors.referrer_address} />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input value={fields.referrer_phone} onChange={(e) => up("referrer_phone", e.target.value)} aria-invalid={!!errors.referrer_phone} />
                <FieldError msg={errors.referrer_phone} />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={fields.referrer_email} onChange={(e) => up("referrer_email", e.target.value)} aria-invalid={!!errors.referrer_email} />
                <FieldError msg={errors.referrer_email} />
              </div>
              <div className="md:col-span-2">
                <Label>Trade union / representative (optional)</Label>
                <Input value={fields.referrer_union} onChange={(e) => up("referrer_union", e.target.value)} />
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <Help>
              The <strong>other party</strong> is normally the employer. Use the{" "}
              <em>full registered name</em> on your payslip / contract — wrong service is
              the #1 reason CCMA matters get postponed.
            </Help>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="md:col-span-2">
                <Label>Employer (full registered name) *</Label>
                <Input value={fields.employer_name} onChange={(e) => up("employer_name", e.target.value)} aria-invalid={!!errors.employer_name} />
                <FieldError msg={errors.employer_name} />
              </div>
              <div className="md:col-span-2">
                <Label>Address (workplace & postal) *</Label>
                <Textarea rows={2} value={fields.employer_address} onChange={(e) => up("employer_address", e.target.value)} aria-invalid={!!errors.employer_address} />
                <FieldError msg={errors.employer_address} />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={fields.employer_phone} onChange={(e) => up("employer_phone", e.target.value)} aria-invalid={!!errors.employer_phone} />
                <FieldError msg={errors.employer_phone} />
              </div>
              <div>
                <Label>Email for service</Label>
                <Input type="email" value={fields.employer_email} onChange={(e) => up("employer_email", e.target.value)} aria-invalid={!!errors.employer_email} />
                <FieldError msg={errors.employer_email} />
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3">
            <Help>
              Pick the <strong>category</strong> that best describes your dispute. The most
              common is <em>unfair dismissal</em> under LRA s191. For misconduct dismissals
              the CCMA applies the Sidumo test (substantive fairness) and Schedule 8 for
              procedural fairness.
            </Help>
            <div>
              <Label>Dispute category *</Label>
              <Select value={fields.dispute_category} onValueChange={(v) => up("dispute_category", v)}>
                <SelectTrigger aria-invalid={!!errors.dispute_category}>
                  <SelectValue placeholder="Choose…" />
                </SelectTrigger>
                <SelectContent>
                  {DISPUTE_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError msg={errors.dispute_category} />
            </div>
            <div>
              <Label>Sub-category / reason *</Label>
              <Input value={fields.dispute_subcategory} onChange={(e) => up("dispute_subcategory", e.target.value)} aria-invalid={!!errors.dispute_subcategory} placeholder='e.g. "dismissal for theft"' />
              <FieldError msg={errors.dispute_subcategory} />
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-3">
            <Help>
              Keep this <strong>factual</strong>. Who, what, when, where, why. Do not argue
              law — that comes later in heads of argument.
            </Help>
            <Textarea
              rows={10}
              value={fields.summary_of_facts}
              onChange={(e) => up("summary_of_facts", e.target.value)}
              maxLength={8000}
              aria-invalid={!!errors.summary_of_facts}
              placeholder="On 12 June 2026 the applicant was summarily dismissed by …"
            />
            <p className="text-[11px] text-muted-foreground">
              {fields.summary_of_facts.trim().length} characters (min 80)
            </p>
            <FieldError msg={errors.summary_of_facts} />
          </div>
        )}

        {step === 5 && (
          <div className="space-y-3">
            <Help>
              State exactly what you want the CCMA to order. For ordinary unfair dismissal,
              compensation is capped at <strong>12 months' remuneration</strong> (LRA s194);
              automatically unfair dismissal is <strong>24 months</strong>. Reinstatement is
              the default primary remedy if you ask for it.
            </Help>
            <div>
              <Label>Relief sought *</Label>
              <Textarea
                rows={3}
                value={fields.outcome_sought}
                onChange={(e) => up("outcome_sought", e.target.value)}
                aria-invalid={!!errors.outcome_sought}
                placeholder="Reinstatement with full back-pay from date of dismissal; alternatively maximum compensation."
              />
              <FieldError msg={errors.outcome_sought} />
            </div>
            <div>
              <Label>Quantum / amount (if known)</Label>
              <Input value={fields.relief_amount} onChange={(e) => up("relief_amount", e.target.value)} placeholder="12 months × R15 000 = R180 000" />
            </div>
          </div>
        )}

        {step === 6 && (
          <div className="space-y-3">
            <Help>
              The CCMA must receive your referral <strong>within 30 calendar days</strong>{" "}
              from the date of dismissal (LRA s191(1)(b)) — 90 days for unfair labour
              practice. If you are late, apply for <strong>condonation</strong> (Melane v
              Santam factors).
            </Help>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label>Date of dismissal / dispute (YYYY-MM-DD) *</Label>
                <Input
                  type="date"
                  value={fields.date_of_dispute}
                  onChange={(e) => up("date_of_dispute", e.target.value)}
                  aria-invalid={!!errors.date_of_dispute}
                />
                <FieldError msg={errors.date_of_dispute} />
              </div>
              <div>
                <Label>Date you became aware (if different)</Label>
                <Input
                  type="date"
                  value={fields.date_aware}
                  onChange={(e) => up("date_aware", e.target.value)}
                  aria-invalid={!!errors.date_aware}
                />
                <FieldError msg={errors.date_aware} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={fields.needs_condonation}
                onChange={(e) => up("needs_condonation", e.target.checked)}
              />
              I am filing late — include condonation application
            </label>
            {fields.needs_condonation && (
              <div>
                <Label>Condonation grounds *</Label>
                <Textarea
                  rows={5}
                  value={fields.condonation_reason}
                  onChange={(e) => up("condonation_reason", e.target.value)}
                  aria-invalid={!!errors.condonation_reason}
                  placeholder="Explain: degree of lateness, reasonable explanation, prospects of success, prejudice, importance."
                />
                <FieldError msg={errors.condonation_reason} />
              </div>
            )}
          </div>
        )}

        {step === 7 && (
          <div className="space-y-3">
            <Help>
              Sign the form and serve a copy on the employer (registered post, sheriff, or
              email with read-receipt). Attach: dismissal letter / hearing outcome, proof of
              service, and the condonation application if late.
            </Help>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label>Region / CCMA office *</Label>
                <Input
                  value={fields.region_office}
                  onChange={(e) => up("region_office", e.target.value)}
                  aria-invalid={!!errors.region_office}
                  placeholder="CCMA Polokwane"
                />
                <FieldError msg={errors.region_office} />
              </div>
              <div>
                <Label>Place signed *</Label>
                <Input
                  value={fields.signed_place}
                  onChange={(e) => up("signed_place", e.target.value)}
                  aria-invalid={!!errors.signed_place}
                />
                <FieldError msg={errors.signed_place} />
              </div>
              <div>
                <Label>Date signed *</Label>
                <Input
                  type="date"
                  value={fields.signed_at}
                  onChange={(e) => up("signed_at", e.target.value)}
                  aria-invalid={!!errors.signed_at}
                />
                <FieldError msg={errors.signed_at} />
              </div>
            </div>
          </div>
        )}

        {step === 8 && (
          <div className="space-y-3">
            <Help>
              Review your answers below, then save the matter or download a completed PDF
              of Form 7.11. The admin still reviews everything before filing.
            </Help>

            {showAll && (errors._form?.length || Object.keys(errors).some((k) => k !== "_form")) ? (
              <div className="space-y-2 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-900">
                <p className="font-semibold">Please fix the following before saving:</p>
                <ul className="ml-4 list-disc space-y-1">
                  {Object.entries(errors)
                    .filter(([k]) => k !== "_form")
                    .map(([k, v]) => (
                      <li key={k}>
                        <strong>{k.replaceAll("_", " ")}:</strong> {v as string}
                      </li>
                    ))}
                  {(errors._form ?? []).map((m, i) => (
                    <li key={`f${i}`}>{m}</li>
                  ))}
                </ul>
              </div>
            ) : showAll ? (
              <p className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
                <CheckCircle2 className="h-3.5 w-3.5" /> All checks passed — ready to save
                or download.
              </p>
            ) : null}

            <pre className="max-h-[400px] overflow-auto whitespace-pre-wrap rounded-md border border-border bg-muted/30 p-3 text-xs">
              {JSON.stringify(fields, null, 2)}
            </pre>
            <div className="flex flex-wrap gap-2">
              <Button onClick={onSave} disabled={saving || downloading} className="bg-emerald-700 hover:bg-emerald-700/90">
                {saving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Form 7.11 to this matter
              </Button>
              <Button
                type="button"
                onClick={onDownloadPdf}
                disabled={saving || downloading}
                variant="outline"
                className="border-navy/30 text-navy hover:bg-navy/5"
              >
                {downloading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                Download completed PDF
              </Button>
            </div>
          </div>
        )}

        {stepErrorCount > 0 && step < 8 && (
          <p className="flex items-center gap-2 text-[11px] text-red-700">
            <AlertTriangle className="h-3 w-3" />
            {stepErrorCount} field{stepErrorCount > 1 ? "s need" : " needs"} attention on
            this step.
          </p>
        )}

        <div className="flex items-center justify-between border-t border-border pt-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
          >
            <ArrowLeft className="mr-1 h-4 w-4" /> Back
          </Button>
          <p className="text-xs text-muted-foreground">
            Step {step + 1} / {STEPS.length}
          </p>
          <Button
            type="button"
            onClick={tryNext}
            disabled={step === STEPS.length - 1}
            className="bg-navy hover:bg-navy/90"
          >
            Next <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </section>

      <p className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
        <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        Your inputs are saved to this matter only — visible to you and NDLCS administrators.
      </p>
    </div>
  );
}
